// LayoutEditor.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Utilities
import {
	cloneBlocksByKey,
	cloneLayout,
	collectBlockKeysForNode,
	getContainerOrientation,
	getNodeBlockKey,
	isLayoutNodeDescendantOf,
} from './pageEditorLayoutUtils';
import {
	createEmptyRichTextDocument,
	normalizeRichTextValue,
	serializeRichTextDocument,
} from 'utilities/RichText';

// Services
import { FORM_FIELD_BLOCK_TYPE } from 'services/forms/FormModel';
import { isContentZoneBlockType } from './contentZoneBlockTypes';

// Hooks
import useLayoutInspectorWidth from './inspector/useLayoutInspectorWidth';
import useLayoutEditorPageState from './page/useLayoutEditorPageState';
import useLayoutEditorBlockModalState from './useLayoutEditorBlockModalState';
import useLayoutEditorBlockMutations from './useLayoutEditorBlockMutations';
import useLayoutEditorDragDrop from './useLayoutEditorDragDrop';
import useLayoutEditorLayoutMutations from './useLayoutEditorLayoutMutations';
import useLayoutEditorPreviewData from './useLayoutEditorPreviewData';
import useLayoutEditorToolbarState from './useLayoutEditorToolbarState';
import useLayoutEditorVideoUpload from './useLayoutEditorVideoUpload';

// CSS
import './LayoutEditor.css';

// Components
import PageVideoStreamingWarning from './warnings/PageVideoStreamingWarning';
import { buildLayoutEditorSlotControls } from './buildLayoutEditorSlotControls';
import {
	buildLayoutEditorToolbarContext,
	resolveLayoutEditorToolbarPosition,
} from './buildLayoutEditorToolbarContext';
import LayoutEditorEmptyState from './emptystate/LayoutEditorEmptyState';
import PageEditorInsertButton from './insertbutton/PageEditorInsertButton';
import LayoutEditorInsertSequence from './insertsequence/LayoutEditorInsertSequence';
import LayoutEditorInspector from './inspector/LayoutEditorInspector';
import LayoutEditorLayoutContainer from './layoutcontainer/LayoutEditorLayoutContainer';
import LayoutEditorModalLayer from './LayoutEditorModalLayer';
import {
	getFieldBlockOptions,
} from './layoutFieldDisplay';
import LayoutEditorSharedToolbarLayer from './sharedtoolbar/LayoutEditorSharedToolbarLayer';
import LayoutEditorSlotNode from './slotcontent/LayoutEditorSlotNode';
import LayoutEditorSlotMenu from './slotmenu/LayoutEditorSlotMenu';
import Confirm from 'components/confirm/Confirm';

function normalizeString(value) {
	return String(value || '').trim();
}

/**
 * LayoutEditor
 *
 * Renders the shared layout/page editor shell used by legacy pages and type-scoped layouts.
 *
 * @param {object|null} editorDocument - Loaded editor document when used from the layout flow.
 *
 * @returns {ReactNode} The rendered editor shell.
 */
const LayoutEditor = ({
	editorDocument = null,
	allowContentZoneBlocks = true,
	allowFieldBlocks = false,
	allowNodeBlocks = true,
	allowVideoBlocks = false,
	blockOverridesByBlockKey = null,
	dashboardKey = null,
	dashboardViewAuthoringEnabled = false,
	dashboardViewModalMode = 'GLOBAL',
	fieldBlockType = 'FIELDS',
	fieldBlockTypeDefOverride = null,
	fieldSelectorLockedFieldKeys = [],
	getTypeOverride = null,
	embeddedInPageHybrid = false,
	fieldBlockTypeKey = '',
	layoutKey = '',
	onDirtyChange = null,
	onItemClick = null,
	onPersistNodeBlock = null,
	onPersistDocument = null,
	onPersistRichTextBlock = null,
	onPersistRollupBlock = null,
	onPersistViewBlock = null,
	onPurgeContentZoneBlockKey = null,
	onDismissPageVideoStreamingWarning = null,
	pagePlaybackContext = null,
	pageViewAuthoringEnabled = false,
	pageViewGetAllTypeDefs = null,
	prePanelContent = null,
	previewNode = null,
	inlineCreateRichTextBlocks = false,
	inlineRichTextPlaceholder = 'Write page copy...',
	richTextValueOverridesByBlockKey = null,
	richTextDocumentLabel = 'page',
	settingsModal = null,
	showPageVideoStreamingWarning = false,
	typeLabelPlural = 'records',
	viewBlockAllowedTypeKeys = null,
	viewBlockChildNodes = [],
	viewBlockDescendantNodes = [],
	viewBlockExistingViewIds = null,
	viewBlockModalCreatedFrom = 'PAGE',
	viewPreviewByBlockKeyOverride = null,
	viewBlockModalEmptyStateSubtitle = '',
	viewBlockTypeKey = '',
	viewBlockModalTitle = 'Add View',
	viewBlockEnforceExplicitProperties = true,
	viewBlockDraftViews = null,
	viewBlockDraftDatabaseLabel = 'this collection',
	viewBlockRelatedTypeKey = '',
	hideSharedToolbar = false,
	inspectorPreview = null,
	showLayoutInspector = false,
	useBlockPlaceholders = false,
	editorIntroHint,
	editorEmptyStateMessage = 'No layout rows yet. Add a block to begin.',
}) => {

	const resolvedDocument = editorDocument || null;
	// ----- HOOKS ----- //

	const {
		handleResizeMouseDown: handleInspectorResizeMouseDown,
		inspectorWidth,
	} = useLayoutInspectorWidth();
	const shouldHideSharedToolbar = hideSharedToolbar || showLayoutInspector;
	const resolvedEditorIntroHint = editorIntroHint === undefined
		? 'Page templates allow you to create a consistent page layout for all items of a given type. Add blocks to the template to create a reusable layout that can be used on every record\'s version of the page. Add content zones to create layout areas that can be edited on a per-record basis.'
		: editorIntroHint;

	// ----- CONTEXT ----- //

	const language = useCurrentLanguage();
	const { getType: getTypeFromContext } = useNodeTypes();
	const getType = getTypeOverride || getTypeFromContext;
	const { user } = useUser();

	// ----- STATE ----- //

	const [openMenuState, setOpenMenuState] = useState(null);
	const [hoveredSlotState, setHoveredSlotState] = useState({
		rowKey: '',
		slotKey: '',
	});
	const [inlineRichTextSlotKey, setInlineRichTextSlotKey] = useState('');
	const [inlineRichTextDraftValue, setInlineRichTextDraftValue] = useState(() => normalizeRichTextValue(null));
	const [contentZoneDeleteConfirm, setContentZoneDeleteConfirm] = useState({
		action: '',
		blockKey: '',
		blockKeys: [],
		open: false,
		parentKey: '',
		slotKey: '',
	});

	// ----- REFS ----- //

	const menuAnchorRef = React.useRef(null);
	// ----- MEMOS ----- //

	const dismissMenu = useCallback(() => {
		menuAnchorRef.current = null;
		setOpenMenuState(null);
	}, []);
	// ----- HOOKS ----- //

	const {
		handleClearToolbar,
		handleOpenToolbar,
		layoutListRef,
		setToolbarRowKey,
		setToolbarSlotKey,
		toolbarAnchor,
		toolbarHeaderRef,
		toolbarRowKey,
		toolbarSlotKey,
		toolbarMeasureState,
	} = useLayoutEditorToolbarState({
		dismissMenu,
		openMenuState,
	});
	const {
		handleClosePagePreviewItem,
		handleClosePageViewTypeModal,
		handleCloseViewBlockModal,
		handleOpenDashboardNewViewModal,
		handleOpenDashboardSavedViewModal,
		handleOpenPagePreviewItem,
		handleOpenPageViewTypeModal,
		handleOpenViewBlockModal,
		handleSelectPageViewType,
		pagePreviewSelectedItem,
		pageViewTypeModalNoun,
		pageViewTypeModalOpen,
		pageViewTypeModalTitle,
		resolvedPageViewDraftTypeKey,
		resolvedPageViewDraftViewType,
		viewBlockModalIntent,
		viewBlockModalOpen,
		viewBlockSlotKey,
	} = useLayoutEditorPageState({
		dismissMenu,
		getType,
	});

	// ----- MEMOS ----- //

	const layout = useMemo(() => cloneLayout(resolvedDocument?.layout), [resolvedDocument?.layout]);
	const blocksByKey = useMemo(() => cloneBlocksByKey(resolvedDocument?.blocksByKey), [resolvedDocument?.blocksByKey]);
	const rootNode = layout.nodes[layout.rootNodeKey] || { key: layout.rootNodeKey, kind: 'stack', children: [] };
	const blockValueOverrides = useMemo(() => {
		if (!blockOverridesByBlockKey || typeof blockOverridesByBlockKey !== 'object' || Array.isArray(blockOverridesByBlockKey)) {
			return {};
		}

		return blockOverridesByBlockKey;
	}, [blockOverridesByBlockKey]);
	const richTextBlockValueOverrides = useMemo(() => {
		if (!richTextValueOverridesByBlockKey || typeof richTextValueOverridesByBlockKey !== 'object' || Array.isArray(richTextValueOverridesByBlockKey)) {
			return {};
		}

		return richTextValueOverridesByBlockKey;
	}, [richTextValueOverridesByBlockKey]);
	function getResolvedBlockValue(blockKey, block) {
		const cleanedBlockKey = String(blockKey || '').trim();
		const sharedBlock = block && typeof block === 'object' && !Array.isArray(block)
			? block
			: null;

		if (
			cleanedBlockKey
			&& Object.prototype.hasOwnProperty.call(blockValueOverrides, cleanedBlockKey)
			&& sharedBlock
		) {
			return {
				...sharedBlock,
				...blockValueOverrides[cleanedBlockKey],
			};
		}

		return sharedBlock;
	}

	const getResolvedRichTextBlockValue = useCallback((blockKey, block) => {
		const layoutValue = normalizeRichTextValue(block?.value);
		const cleanedBlockKey = String(blockKey || '').trim();

		if (
			cleanedBlockKey
			&& Object.prototype.hasOwnProperty.call(richTextBlockValueOverrides, cleanedBlockKey)
		) {
			const overrideValue = normalizeRichTextValue(richTextBlockValueOverrides[cleanedBlockKey]);
			const layoutSerialized = serializeRichTextDocument(layoutValue.content);
			const overrideSerialized = serializeRichTextDocument(overrideValue.content);
			const emptySerialized = serializeRichTextDocument(createEmptyRichTextDocument());

			if (layoutSerialized === emptySerialized && overrideSerialized !== emptySerialized) {
				return overrideValue;
			}
		}

		return layoutValue;
	}, [richTextBlockValueOverrides]);
	// ----- HOOKS ----- //

	const {
		fieldBlockDraftFieldKeys,
		fieldBlockModalOpen,
		fieldBlockSlotKey,
		handleCloseFieldBlockModal,
		handleCloseNodeBlockModal,
		handleCloseRichTextBlockModal,
		handleCloseRollupBlockModal,
		handleFieldBlockDraftFieldKeysChange,
		handleOpenFieldBlockModal,
		handleOpenNodeBlockModal,
		handleOpenRichTextBlockModal,
		handleOpenRollupBlockModal,
		nodeBlockModalOpen,
		nodeBlockSlotKey,
		richTextBlockModalOpen,
		richTextBlockSlotKey,
		rollupBlockModalOpen,
		rollupBlockSlotKey,
	} = useLayoutEditorBlockModalState({
		blocksByKey,
		dismissMenu,
		getResolvedBlockValue,
		layout,
		onClearInlineRichTextEditing: () => setInlineRichTextSlotKey(''),
	});

	const rootChildren = Array.isArray(rootNode?.children) ? rootNode.children : [];
	// ----- MEMOS ----- //

	const assignedBlockCount = useMemo(() => {
		return collectBlockKeysForNode(layout, layout.rootNodeKey)
			.filter((blockKey) => Boolean(blocksByKey[blockKey]))
			.length;
	}, [blocksByKey, layout]);
	const resolvedFieldBlockTypeKey = String(fieldBlockTypeKey || previewNode?.type || '').trim();
	const fieldBlockTypeDef = fieldBlockTypeDefOverride
		|| (resolvedFieldBlockTypeKey ? getType(resolvedFieldBlockTypeKey) || null : null);
	const fieldBlockOptions = useMemo(() => getFieldBlockOptions(fieldBlockTypeDef), [fieldBlockTypeDef]);
	const fieldSelectorAvailableFields = useMemo(() => {
		return fieldBlockOptions.map((option) => option.fieldDef).filter(Boolean);
	}, [fieldBlockOptions]);
	const resolvedViewBlockAllowedTypeKeys = useMemo(() => {
		if (!Array.isArray(viewBlockAllowedTypeKeys)) {
			return null;
		}

		const cleanedKeys = Array.from(new Set(
			viewBlockAllowedTypeKeys
				.map((typeKey) => normalizeString(typeKey))
				.filter(Boolean)
		));

		return cleanedKeys.length ? cleanedKeys : [];
	}, [viewBlockAllowedTypeKeys]);
	const pageViewTypeKeysForPicker = useMemo(() => {
		if (!pageViewAuthoringEnabled) {
			return resolvedViewBlockAllowedTypeKeys;
		}

		if (!Array.isArray(resolvedViewBlockAllowedTypeKeys)) {
			return null;
		}

		return resolvedViewBlockAllowedTypeKeys.length ? resolvedViewBlockAllowedTypeKeys : null;
	}, [pageViewAuthoringEnabled, resolvedViewBlockAllowedTypeKeys]);
	const activeViewSlotNode = layout.nodes[normalizeString(viewBlockSlotKey)] || null;
	const activeViewBlockKey = getNodeBlockKey(activeViewSlotNode);
	const activeViewBlock = activeViewBlockKey ? blocksByKey[activeViewBlockKey] || null : null;
	const activeResolvedViewBlock = getResolvedBlockValue(activeViewBlockKey, activeViewBlock);
	const activeViewHasLocalDefinition = Boolean(activeResolvedViewBlock?.view && typeof activeResolvedViewBlock.view === 'object');
	const resolvedViewBlockTypeKey = String(
		resolvedPageViewDraftTypeKey
		|| activeResolvedViewBlock?.typeKey
		|| viewBlockTypeKey
		|| previewNode?.type
		|| ''
	).trim();
	const useRelatedViewSelections = Array.isArray(resolvedViewBlockAllowedTypeKeys);
	const useDraftViewBlockPicker = Array.isArray(viewBlockDraftViews);
	const useLocalViewDefinitions = !useDraftViewBlockPicker && (
		pageViewAuthoringEnabled
		|| activeViewHasLocalDefinition
		|| Boolean(resolvedPageViewDraftTypeKey)
		|| (Boolean(resolvedViewBlockTypeKey) && !useRelatedViewSelections)
	);
	const resolvedInitialPageViewType = String(
		resolvedPageViewDraftViewType
		|| activeResolvedViewBlock?.view?.type
		|| getType(resolvedViewBlockTypeKey)?.defaultView
		|| 'LIST'
	).trim().toUpperCase();
	const baseKey = String(resolvedDocument?.baseKey || '').trim();
	// ----- HOOKS ----- //

	const {
		nodeByKey: internalNodeByKey,
		rollupPreviewBySelectionKey: internalRollupPreviewBySelectionKey,
		viewPreviewByBlockKey: internalViewPreviewByBlockKey,
	} = useLayoutEditorPreviewData({
		blocksByKey,
		getType,
		previewNode,
		previewNodeChildNodes: viewBlockChildNodes,
		previewNodeDescendantNodes: viewBlockDescendantNodes,
		userKey: user?.key || null,
		baseKey,
	});
	const nodeByKey = internalNodeByKey;
	const rollupPreviewBySelectionKey = internalRollupPreviewBySelectionKey;
	const viewPreviewByBlockKey = viewPreviewByBlockKeyOverride || internalViewPreviewByBlockKey;
	// ----- MEMOS ----- //

	const activeSlotShortcutContext = useMemo(() => {
		const containerKey = String(toolbarRowKey || '').trim();
		const slotKey = String(toolbarSlotKey || '').trim();
		if (!containerKey || !slotKey) return null;

		const containerNode = layout.nodes[containerKey] || null;
		const activeSlotNode = layout.nodes[slotKey] || null;
		if (!containerNode || !activeSlotNode || activeSlotNode.kind !== 'block_slot') return null;

		const block = blocksByKey[getNodeBlockKey(activeSlotNode)] || null;
		const blockType = String(block?.type || '').trim().toUpperCase();

		return {
			activeSlotNode,
			blockType,
			containerNode,
		};
	}, [blocksByKey, layout.nodes, toolbarRowKey, toolbarSlotKey]);

	/**
	 * persistLayout
	 *
	 * Saves the current layout tree back to the page document.
	 *
	 * @param {object} nextLayout - Layout tree to persist.
	 *
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const persistLayout = useCallback(async (nextLayout) => {
		if (!resolvedDocument?.key) return;
		if (typeof onPersistDocument !== 'function') return;

		await onPersistDocument(nextLayout, blocksByKey);
	}, [blocksByKey, onPersistDocument, resolvedDocument?.key]);

	const persistPage = useCallback(async (nextLayout, nextBlocksByKey) => {
		if (!resolvedDocument?.key) return;
		if (typeof onPersistDocument !== 'function') return;

		await onPersistDocument(nextLayout, nextBlocksByKey);
	}, [onPersistDocument, resolvedDocument?.key]);

	/**
	 * handleSetMenuOpen
	 *
	 * Handles set menu open.
	 *
	 * @param {unknown} nextOpen - nextOpen.
	 */
	function handleSetMenuOpen(nextOpen) {
		const resolvedOpen = typeof nextOpen === 'function'
			? nextOpen(Boolean(openMenuState))
			: nextOpen;

		if (!resolvedOpen) {
			dismissMenu();
		}
	}
	// ----- HOOKS ----- //

	const {
		dragOverTarget,
		draggingNodeKey,
		handleDragEnd,
		handleDragStart,
		handleEmptySlotDragOver,
		handleEmptySlotDrop,
		handleInsertDragOver,
		handleInsertDrop,
		handleStickyDropZoneDragOver,
		handleStickyDropZoneDrop,
	} = useLayoutEditorDragDrop({
		dismissMenu,
		handleClearToolbar,
		layout,
		persistLayout,
	});

	const {
		handleAddSlotToContainer,
		handleDeleteChildFromContainer: deleteChildFromContainer,
		handleInsertRowAtRoot,
		handleInsertSlotInContainer,
		handleMoveChildInContainer,
		handleSplitSlot,
	} = useLayoutEditorLayoutMutations({
		assignedBlockCount,
		blocksByKey,
		layout,
		persistLayout,
		persistPage,
		setToolbarRowKey,
		setToolbarSlotKey,
	});
	const {
		handleAssignContentZoneBlock,
		handleAssignCoverBlock,
		handleAssignDiscussionBlock,
		handleAssignFieldBlock,
		handleAssignNodeBlock,
		handleAssignRollupBlock,
		handleAssignVideoBlock,
		handleAssignViewBlock,
		handleCreateInlineRichTextBlock,
		handleInlineRichTextBlur,
		handleRemoveBlockFromSlot: removeBlockFromSlot,
		handleSaveRichTextBlock,
		handleSetNodeBlockPresentation,
		handleStartInlineRichTextEditing,
	} = useLayoutEditorBlockMutations({
		assignedBlockCount,
		blocksByKey,
		fieldBlockSlotKey,
		fieldBlockType,
		handleCloseFieldBlockModal,
		handleCloseNodeBlockModal,
		handleCloseRichTextBlockModal,
		handleCloseRollupBlockModal,
		handleCloseViewBlockModal,
		layout,
		nodeBlockSlotKey,
		onPersistNodeBlock,
		onPersistRichTextBlock,
		onPersistRollupBlock,
		onPersistViewBlock,
		persistPage,
		resolvedViewBlockTypeKey,
		richTextBlockSlotKey,
		rollupBlockSlotKey,
		setInlineRichTextDraftValue,
		setInlineRichTextSlotKey,
		useLocalViewDefinitions,
		viewBlockSlotKey,
	});

	const {
		onVideoFileChange,
		triggerVideoUpload,
		uploadErrorBySlotKey,
		uploadingSlotKey,
		videoUploadAccept,
		videoUploadInputRef,
	} = useLayoutEditorVideoUpload({
		onAssignVideoBlock: handleAssignVideoBlock,
		previewNode,
		user,
	});

	// ----- MEMOS ----- //

	const hasUnsavedInlineRichText = useMemo(() => {
		const cleanedSlotKey = normalizeString(inlineRichTextSlotKey);
		if (!cleanedSlotKey) {
			return false;
		}

		const slotNode = layout.nodes[cleanedSlotKey];
		if (!slotNode) {
			return false;
		}

		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] : null;
		if (!block || String(block?.type || '').trim().toUpperCase() !== 'RICH_TEXT') {
			return false;
		}

		const savedValue = getResolvedRichTextBlockValue(blockKey, block);
		const draftSerialized = serializeRichTextDocument(normalizeRichTextValue(inlineRichTextDraftValue).content);
		const savedSerialized = serializeRichTextDocument(normalizeRichTextValue(savedValue).content);

		return draftSerialized !== savedSerialized;
	}, [
		blocksByKey,
		getResolvedRichTextBlockValue,
		inlineRichTextDraftValue,
		inlineRichTextSlotKey,
		layout.nodes,
	]);

	const hasVideoUploadInProgress = Boolean(normalizeString(uploadingSlotKey));
	const isEditorDirty = hasUnsavedInlineRichText || hasVideoUploadInProgress;

	// ----- EFFECTS ----- //

	useEffect(() => {
		if (typeof onDirtyChange !== 'function') {
			return;
		}

		onDirtyChange(isEditorDirty);
	}, [isEditorDirty, onDirtyChange]);

	// ----- MEMOS ----- //

	const collectContentZoneBlockKeysForNode = useCallback((nodeKey) => {
		const cleanedNodeKey = normalizeString(nodeKey);
		if (!cleanedNodeKey) {
			return [];
		}

		return collectBlockKeysForNode(layout, cleanedNodeKey)
			.filter((blockKey) => isContentZoneBlockType(blocksByKey[blockKey]));
	}, [blocksByKey, layout]);

	const requestContentZoneRemovalConfirm = useCallback(({
		action,
		blockKeys = [],
		parentKey = '',
		slotKey = '',
	}) => {
		const cleanedBlockKeys = Array.from(new Set(
			blockKeys.map((blockKey) => normalizeString(blockKey)).filter(Boolean),
		));

		if (!cleanedBlockKeys.length || typeof onPurgeContentZoneBlockKey !== 'function') {
			return false;
		}

		setContentZoneDeleteConfirm({
			action,
			blockKey: cleanedBlockKeys[0] || '',
			blockKeys: cleanedBlockKeys,
			open: true,
			parentKey: normalizeString(parentKey),
			slotKey: normalizeString(slotKey),
		});

		return true;
	}, [onPurgeContentZoneBlockKey]);

	// ----- HANDLERS ----- //

	/**
 * Handles remove block from slot.
 *
 * @param {*} slotKey
 */

	const handleRemoveBlockFromSlot = useCallback(async (slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) {
			return;
		}

		const slotNode = layout.nodes[cleanedSlotKey];
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;

		if (
			isContentZoneBlockType(block)
			&& requestContentZoneRemovalConfirm({
				action: 'remove',
				blockKeys: blockKey ? [blockKey] : [],
				slotKey: cleanedSlotKey,
			})
		) {
			return;
		}

		await removeBlockFromSlot(cleanedSlotKey);
	}, [blocksByKey, layout, removeBlockFromSlot, requestContentZoneRemovalConfirm]);

	/**
 * Handles delete child from container.
 *
 * @param {*} parentKey
 * @param {*} childKey
 */
	const handleDeleteChildFromContainer = useCallback(async (parentKey, childKey) => {
		const cleanedParentKey = normalizeString(parentKey);
		const cleanedChildKey = normalizeString(childKey);
		if (!cleanedParentKey || !cleanedChildKey) {
			return;
		}

		const contentZoneBlockKeys = collectContentZoneBlockKeysForNode(cleanedChildKey);
		if (contentZoneBlockKeys.length && requestContentZoneRemovalConfirm({
			action: 'delete',
			blockKeys: contentZoneBlockKeys,
			parentKey: cleanedParentKey,
			slotKey: cleanedChildKey,
		})) {
			return;
		}

		await deleteChildFromContainer(cleanedParentKey, cleanedChildKey);
	}, [collectContentZoneBlockKeysForNode, deleteChildFromContainer, requestContentZoneRemovalConfirm]);

	/**
 * Handles confirm content zone removal.
 */
	const handleConfirmContentZoneRemoval = useCallback(async () => {
		const {
			action,
			blockKeys,
			parentKey,
			slotKey,
		} = contentZoneDeleteConfirm;

		setContentZoneDeleteConfirm({
			action: '',
			blockKey: '',
			blockKeys: [],
			open: false,
			parentKey: '',
			slotKey: '',
		});

		try {
			if (action === 'remove') {
				await removeBlockFromSlot(slotKey);
			} else if (action === 'delete') {
				await deleteChildFromContainer(parentKey, slotKey);
			}

			if (typeof onPurgeContentZoneBlockKey === 'function') {
				for (const blockKey of blockKeys) {
					const cleanedBlockKey = normalizeString(blockKey);
					if (!cleanedBlockKey) {
						continue;
					}

					await onPurgeContentZoneBlockKey(cleanedBlockKey);
				}
			}
		} catch (error) {
			console.error('Failed to remove content zone block', error);
		}
	}, [
		contentZoneDeleteConfirm,
		deleteChildFromContainer,
		onPurgeContentZoneBlockKey,
		removeBlockFromSlot,
	]);

	// ----- EFFECTS ----- //

	useEffect(() => {
		function isEditableShortcutTarget(target) {
			if (!target || typeof target.closest !== 'function') return false;

			return Boolean(target.closest([
				'button',
				'input',
				'select',
				'textarea',
				'[contenteditable="true"]',
				'[role="textbox"]',
				'.ProseMirror',
			].join(', ')));
		}

		/**
		 * handleDocumentKeyDown
		 *
		 * Handles document key down.
		 *
		 * @param {object} event - event.
		 */
		function handleDocumentKeyDown(event) {
			if (event.defaultPrevented || event.repeat) return;
			if (event.key !== 'Backspace' && event.key !== 'Delete') return;
			if (event.metaKey || event.ctrlKey || event.altKey) return;
			if (openMenuState || fieldBlockModalOpen || nodeBlockModalOpen || richTextBlockModalOpen || rollupBlockModalOpen || pageViewTypeModalOpen || viewBlockModalOpen) {
				return;
			}
			if (isEditableShortcutTarget(event.target)) return;

			if (!activeSlotShortcutContext) return;
			if (activeSlotShortcutContext.blockType === 'RICH_TEXT') return;

			event.preventDefault();
			void handleDeleteChildFromContainer(activeSlotShortcutContext.containerNode.key, activeSlotShortcutContext.activeSlotNode.key);
		}

		document.addEventListener('keydown', handleDocumentKeyDown);

		return () => {
			document.removeEventListener('keydown', handleDocumentKeyDown);
		};
	}, [
		activeSlotShortcutContext,
		fieldBlockModalOpen,
		handleDeleteChildFromContainer,
		nodeBlockModalOpen,
		openMenuState,
		pageViewTypeModalOpen,
		richTextBlockModalOpen,
		rollupBlockModalOpen,
		toolbarRowKey,
		toolbarSlotKey,
		viewBlockModalOpen,
	]);

	/**
	 * handleMenuAction
	 *
	 * Handles menu action.
	 *
	 * @param {unknown} callback - callback.
	 * @returns {Promise<void>} Resolves when the handler completes.
	 */
	async function handleMenuAction(callback) {
		menuAnchorRef.current = null;
		setOpenMenuState(null);
		await callback();
	}

	/**
	 * handleSetHoveredSlot
	 *
	 * Handles set hovered slot.
	 *
	 * @param {string} rowKey - rowKey.
	 * @param {string} slotKey - slotKey.
	 */
	function handleSetHoveredSlot(rowKey, slotKey) {
		const cleanedRowKey = String(rowKey || '').trim();
		const cleanedSlotKey = String(slotKey || '').trim();
		if (!cleanedRowKey || !cleanedSlotKey) return;

		setHoveredSlotState({
			rowKey: cleanedRowKey,
			slotKey: cleanedSlotKey,
		});
	}

	/**
	 * handleClearHoveredSlot
	 *
	 * Handles clear hovered slot.
	 *
	 * @param {string} rowKey - rowKey.
	 * @param {string} slotKey - slotKey.
	 */
	function handleClearHoveredSlot(rowKey, slotKey) {
		const cleanedRowKey = String(rowKey || '').trim();
		const cleanedSlotKey = String(slotKey || '').trim();

		setHoveredSlotState((current) => {
			if (cleanedRowKey && current.rowKey !== cleanedRowKey) return current;
			if (cleanedSlotKey && current.slotKey !== cleanedSlotKey) return current;

			return {
				rowKey: '',
				slotKey: '',
			};
		});
	}

	function getRowToolbarPosition(rowKey) {
		return resolveLayoutEditorToolbarPosition({
			blocksByKey,
			inlineRichTextSlotKey,
			layout,
			rowKey,
			toolbarAnchor,
			toolbarMeasureState,
			toolbarRowKey,
			toolbarSlotKey,
		});
	}

	function getActiveToolbarContext() {
		return buildLayoutEditorToolbarContext({
			getSlotControls,
			handleAddSlotToContainer,
			handleDeleteChildFromContainer,
			handleMoveChildInContainer,
			layout,
			toolbarRowKey,
			toolbarSlotKey,
		});
	}

	function renderSharedToolbar() {
		if (shouldHideSharedToolbar) return null;

		const toolbarContext = getActiveToolbarContext();
		const toolbarPosition = toolbarContext
			? getRowToolbarPosition(toolbarContext.containerKey)
			: null;

		return (
			<LayoutEditorSharedToolbarLayer
				blocksByKey={blocksByKey}
				inlineRichTextSlotKey={inlineRichTextSlotKey}
				placement={toolbarPosition?.placement || 'below'}
				positionStyle={toolbarPosition
					? {
						left: toolbarPosition.left,
						top: toolbarPosition.top,
					}
					: {}}
				toolbarContext={toolbarContext}
				toolbarRef={toolbarHeaderRef}
			/>
		);
	}

	/**
	 * handleOpenViewBlockFlow
	 *
	 * Handles open view block flow.
	 *
	 * @param {string} slotKey - slotKey.
	 */
	function handleOpenViewBlockFlow(slotKey) {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		if (!pageViewAuthoringEnabled) {
			handleOpenViewBlockModal(cleanedSlotKey);
			return;
		}

		const slotNode = layout.nodes[cleanedSlotKey] || null;
		const blockKey = getNodeBlockKey(slotNode);
		const block = blockKey ? blocksByKey[blockKey] || null : null;
		const hasEmbeddedView = Boolean(block?.view && typeof block.view === 'object');
		const hasSavedViewReference = Boolean(normalizeString(block?.viewId));

		if (hasEmbeddedView || hasSavedViewReference) {
			handleOpenViewBlockModal(cleanedSlotKey);
			return;
		}

		handleOpenPageViewTypeModal(cleanedSlotKey, '');
	}

	function getSlotControls(slotNode, options = {}) {
		return buildLayoutEditorSlotControls({
			allowContentZoneBlocks,
			allowFieldBlocks,
			allowNodeBlocks,
			allowVideoBlocks,
			assignedBlockCount,
			blocksByKey,
			fieldBlockOptions,
			handleAssignContentZoneBlock,
			handleAssignCoverBlock,
			handleAssignDiscussionBlock,
			handleCreateInlineRichTextBlock,
			handleMenuAction,
			handleOpenDashboardNewViewModal,
			handleOpenDashboardSavedViewModal,
			handleOpenFieldBlockModal,
			handleOpenNodeBlockModal,
			handleOpenPageViewTypeModal,
			handleOpenRichTextBlockModal,
			handleOpenRollupBlockModal,
			handleOpenViewBlockModal: handleOpenViewBlockFlow,
			handleRemoveBlockFromSlot,
			handleSetNodeBlockPresentation,
			handleTriggerVideoUpload: triggerVideoUpload,
			inlineCreateRichTextBlocks,
			options: {
				...options,
				handleSplitSlot,
			},
			dashboardViewAuthoringEnabled,
			pageViewAuthoringEnabled,
			resolvedViewBlockAllowedTypeKeys,
			slotNode,
		});
	}

	function renderContainerNode(containerNode, options = {}) {
		const containerChildren = Array.isArray(containerNode?.children) ? containerNode.children : [];
		const childOrientation = getContainerOrientation(containerNode.kind);
		const isDragging = draggingNodeKey === containerNode?.key;
		const containsActiveInlineRichText = Boolean(normalizeString(inlineRichTextSlotKey))
			&& isLayoutNodeDescendantOf(layout, inlineRichTextSlotKey, containerNode.key);

		return (
			<LayoutEditorLayoutContainer
				containerChildren={containerChildren}
				containerNode={containerNode}
				childOrientation={childOrientation}
				suspendDrag={containsActiveInlineRichText}
				getInsertButtonProps={(insertIndex) => {
					const insertTargetKey = `${containerNode.key}:insert:${insertIndex}`;

					return {
						isDropActive: dragOverTarget?.edge === 'insert'
							&& dragOverTarget?.insertIndex === insertIndex
							&& dragOverTarget?.parentKey === containerNode.key
							&& dragOverTarget?.targetNodeKey === insertTargetKey,
						onDragOver: (event) => handleInsertDragOver(containerNode.key, insertIndex, insertTargetKey, event),
						onDrop: (event) => handleInsertDrop(containerNode.key, insertIndex, event),
					};
				}}
				isDragging={isDragging}
				isDropActive={false}
				key={containerNode.key}
				renderChild={(childKey, childIndex) => {
					const childNode = layout.nodes[childKey];
					if (!childNode) return null;

					return renderLayoutNode(childKey, {
						itemIndex: childIndex,
						orientation: childOrientation,
						parentKey: containerNode.key,
						parentKind: containerNode.kind,
					});
				}}
				onBlurCapture={(event) => {
					if (event.currentTarget.contains(event.relatedTarget)) return;

					const relatedTarget = event.relatedTarget;
					if (relatedTarget && typeof relatedTarget.closest === 'function') {
						if (relatedTarget.closest('.layout-editor-inspector')) return;
						if (relatedTarget.closest('.layout-editor-inspector-shell')) return;
					}

					handleClearToolbar(containerNode.key);
				}}
				onContainerClick={(event) => {
					if (typeof event?.target?.closest === 'function' && event.target.closest('.layout-editor-shared-toolbar-container')) {
						return;
					}

					handleOpenToolbar(containerNode.key, '', event);
				}}
				onDragEnd={handleDragEnd}
				onDragStart={(event) => handleDragStart(containerNode.key, event)}
				onInsertAt={(insertIndex) => handleInsertSlotInContainer(containerNode.key, insertIndex)}
			/>
		);
	}

	function renderLayoutNode(nodeKey, options = {}) {
		const layoutNode = layout.nodes[nodeKey];
		if (!layoutNode) return null;

		if (layoutNode.kind === 'block_slot') {
			return renderSlotNode(layoutNode, options);
		}

		if (layoutNode.kind === 'row' || layoutNode.kind === 'stack') {
			return renderContainerNode(layoutNode, options);
		}

		return null;
	}

	function renderSlotNode(slotNode, options = {}) {
		const parentKey = String(options?.parentKey || '').trim();
		return (
			<LayoutEditorSlotNode
				embeddedInPageHybrid={embeddedInPageHybrid}
				fieldRowUsePanelSurface={normalizeString(fieldBlockType).toUpperCase() === FORM_FIELD_BLOCK_TYPE}
				handlers={{
					handleClearHoveredSlot,
					handleDragEnd,
					handleDragStart,
					handleEmptySlotDragOver,
					handleEmptySlotDrop,
					handleInlineRichTextBlur,
					handleOpenPagePreviewItem,
					handleOpenToolbar,
					handleSetHoveredSlot,
					handleStartInlineRichTextEditing,
					setInlineRichTextDraftValue,
				}}
				interactionState={{
					dragOverTarget,
					draggingNodeKey,
					hoveredSlotState,
					inlineRichTextDraftValue,
					inlineRichTextPlaceholder,
					inlineRichTextSlotKey,
					toolbarRowKey,
					toolbarSlotKey,
				}}
				key={slotNode?.key}
				language={language}
				parentKey={parentKey}
				previewData={{
					fieldBlockTypeDef,
					nodeByKey,
					pagePlaybackContext,
					previewNode,
					rollupPreviewBySelectionKey,
					uploadErrorBySlotKey,
					uploadingSlotKey,
					viewPreviewByBlockKey,
				}}
				resolvers={{
					getResolvedBlockValue,
					getResolvedRichTextBlockValue,
					getSlotControls,
					getType,
				}}
				slotNode={slotNode}
				slotOptions={options}
				typeLabelPlural={typeLabelPlural}
				useBlockPlaceholders={useBlockPlaceholders}
				viewBlockEnforceExplicitProperties={viewBlockEnforceExplicitProperties}
				onItemClick={onItemClick}
			/>
		);
	}
	// ----- RENDER ----- //

	return (

		<div
			className={[
				'layout-editor-container',
				embeddedInPageHybrid ? 'layout-editor-container-hybrid-embed' : '',
			].filter(Boolean).join(' ')}
		>

			{prePanelContent}

			{showPageVideoStreamingWarning ? (
				<PageVideoStreamingWarning onDismiss={onDismissPageVideoStreamingWarning} />
			) : null}

			{/* VIDEO UPLOAD INPUT */}
			<input
				className="layout-editor-video-upload-input"
				accept={videoUploadAccept}
				ref={videoUploadInputRef}
				type="file"
				onChange={onVideoFileChange}
			/>

			{/* BASE */}
			<div
				className={[
					'layout-editor-base',
					showLayoutInspector ? 'has-inspector' : '',
				].filter(Boolean).join(' ')}
			>

				{/* CANVAS */}
				<div className="layout-editor-canvas">

					{/* PANEL */}
					<div
						className="layout-editor-panel"
						onDragOver={handleStickyDropZoneDragOver}
						onDropCapture={handleStickyDropZoneDrop}
					>

						{/* HINT */}
						{resolvedEditorIntroHint && !showLayoutInspector && !dashboardViewAuthoringEnabled ? (

							<div className="layout-editor-hint">
								{resolvedEditorIntroHint}
							</div>
						) : null}

						{/* LAYOUT LIST */}
						<div
							className="layout-editor-layout-list"
							ref={layoutListRef}
						>
							{rootChildren.length > 0 ? (
								<>
									<LayoutEditorInsertSequence
										childKeys={rootChildren}
										getInsertButtonProps={(insertIndex) => {
											const insertTargetKey = `${layout.rootNodeKey}:insert:${insertIndex}`;

											return {
												isDropActive: dragOverTarget?.edge === 'insert'
													&& dragOverTarget?.insertIndex === insertIndex
													&& dragOverTarget?.parentKey === layout.rootNodeKey
													&& dragOverTarget?.targetNodeKey === insertTargetKey,
												onDragOver: (event) => handleInsertDragOver(layout.rootNodeKey, insertIndex, insertTargetKey, event),
												onDrop: (event) => handleInsertDrop(layout.rootNodeKey, insertIndex, event),
											};
										}}
										keyPrefix={layout.rootNodeKey}
										orientation="vertical"
										renderChild={(nodeKey, index) => renderLayoutNode(nodeKey, {
											itemIndex: index,
											orientation: 'vertical',
											parentKey: layout.rootNodeKey,
											parentKind: 'stack',
										})}
										onInsertAt={handleInsertRowAtRoot}
									/>
									{renderSharedToolbar()}
								</>
							) : (
								<>
									<LayoutEditorEmptyState message={editorEmptyStateMessage} />
									{!shouldHideSharedToolbar ? (
										<PageEditorInsertButton onClick={() => handleInsertRowAtRoot(0)} />
									) : null}
								</>
							)}
						</div>
					</div>
				</div>

				{showLayoutInspector ? (
					<div
						className="layout-editor-inspector-shell"
						style={{
							width: inspectorWidth,
						}}
					>

						{/* INSPECTOR RESIZE HANDLE */}
						<div
							className="layout-editor-inspector-resize-handle"
							role="separator"
							aria-orientation="vertical"
							aria-label="Resize layout inspector"
							onMouseDown={handleInspectorResizeMouseDown}
						>

							{/* INSPECTOR RESIZE HANDLE TRACK */}
							<div className="layout-editor-inspector-resize-handle-track" />
						</div>

						<LayoutEditorInspector
							inspectorPreview={inspectorPreview}
							toolbarContext={getActiveToolbarContext()}
						/>
					</div>
				) : null}
			</div>

			<LayoutEditorModalLayer
				allowNodeBlocks={allowNodeBlocks}
				blocksByKey={blocksByKey}
				dashboardKey={dashboardKey}
				dashboardViewAuthoringEnabled={dashboardViewAuthoringEnabled}
				dashboardViewModalMode={dashboardViewModalMode}
				fieldBlockDraftFieldKeys={fieldBlockDraftFieldKeys}
				fieldBlockModalOpen={fieldBlockModalOpen}
				fieldSelectorAvailableFields={fieldSelectorAvailableFields}
				fieldSelectorLockedFieldKeys={fieldSelectorLockedFieldKeys}
				getResolvedBlockValue={getResolvedBlockValue}
				getResolvedRichTextBlockValue={getResolvedRichTextBlockValue}
				getType={getType}
				handleAssignFieldBlock={handleAssignFieldBlock}
				handleAssignNodeBlock={handleAssignNodeBlock}
				handleAssignRollupBlock={handleAssignRollupBlock}
				handleAssignViewBlock={handleAssignViewBlock}
				handleCloseFieldBlockModal={handleCloseFieldBlockModal}
				handleCloseNodeBlockModal={handleCloseNodeBlockModal}
				handleClosePagePreviewItem={handleClosePagePreviewItem}
				handleClosePageViewTypeModal={handleClosePageViewTypeModal}
				handleCloseRichTextBlockModal={handleCloseRichTextBlockModal}
				handleCloseRollupBlockModal={handleCloseRollupBlockModal}
				handleCloseViewBlockModal={handleCloseViewBlockModal}
				handleFieldBlockDraftFieldKeysChange={handleFieldBlockDraftFieldKeysChange}
				handleRemoveBlockFromSlot={handleRemoveBlockFromSlot}
				handleSaveRichTextBlock={handleSaveRichTextBlock}
				handleSelectPageViewType={handleSelectPageViewType}
				layout={layout}
				nodeByKey={nodeByKey}
				nodeBlockModalOpen={nodeBlockModalOpen}
				nodeBlockSlotKey={nodeBlockSlotKey}
				pagePreviewSelectedItem={pagePreviewSelectedItem}
				pageViewGetAllTypeDefs={pageViewGetAllTypeDefs}
				pageViewHostTypeKey={resolvedFieldBlockTypeKey}
				pageViewTypeModalNoun={pageViewTypeModalNoun}
				pageViewTypeModalOpen={pageViewTypeModalOpen}
				pageViewTypeModalTitle={pageViewTypeModalTitle}
				resolvedPageViewDraftTypeKey={resolvedPageViewDraftTypeKey}
				resolvedPageViewDraftViewType={resolvedInitialPageViewType}
				resolvedViewBlockAllowedTypeKeys={pageViewTypeKeysForPicker}
				resolvedViewBlockTypeKey={resolvedViewBlockTypeKey}
				richTextBlockModalOpen={richTextBlockModalOpen}
				richTextBlockSlotKey={richTextBlockSlotKey}
				richTextDocumentLabel={richTextDocumentLabel}
				rollupBlockModalOpen={rollupBlockModalOpen}
				settingsModal={settingsModal}
				useLocalViewDefinitions={useLocalViewDefinitions}
				viewBlockModalCreatedFrom={viewBlockModalCreatedFrom}
				viewBlockModalEmptyStateSubtitle={viewBlockModalEmptyStateSubtitle}
				viewBlockModalOpen={viewBlockModalOpen}
				viewBlockModalTitle={viewBlockModalTitle}
				viewBlockSlotKey={viewBlockSlotKey}
				viewBlockDraftViews={viewBlockDraftViews}
				viewBlockDraftDatabaseLabel={viewBlockDraftDatabaseLabel}
				viewBlockExistingViewIds={viewBlockExistingViewIds}
				viewBlockModalIntent={viewBlockModalIntent}
				viewBlockRelatedTypeKey={viewBlockRelatedTypeKey}
				baseKey={baseKey}
			/>

			<LayoutEditorSlotMenu
				anchorRef={menuAnchorRef}
				isOpen={Boolean(openMenuState)}
				menuState={openMenuState}
				setOpen={handleSetMenuOpen}
			/>

			<Confirm
				confirmLabel="Delete Zone"
				isOpen={contentZoneDeleteConfirm.open}
				message="Per-record content for this section will be permanently deleted on every page instance that uses this layout."
				setOpen={(nextOpen) => {
					if (nextOpen) {
						return;
					}

					setContentZoneDeleteConfirm({
						action: '',
						blockKey: '',
						blockKeys: [],
						open: false,
						parentKey: '',
						slotKey: '',
					});
				}}
				title="Delete Content Zone?"
				onConfirm={handleConfirmContentZoneRemoval}
			/>
		</div>

	);
};

const PageEditor = LayoutEditor;

export { LayoutEditor, PageEditor };

// Dev Tools
LayoutEditor.displayName = 'LayoutEditor';

export default LayoutEditor;
